import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const emptyForm = {
  id: '', name: '', receipt_date: '', bank_transaction_id: '',
  bank_transaction_date: '', receipt_amount: '', balance_amount: '',
  reconciled: '', income_type_id: '', comments: '',
};

export default function ReceiptsPanel({ onClose }) {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [showIncomeType, setShowIncomeType] = useState(false);
  const [disableReconciled, setDisableReconciled] = useState(false);

  const [receipts, setReceipts] = useState([]);
  const [receiptTypes, setReceiptTypes] = useState([]);
  const [searchFilters, setSearchFilters] = useState({ name: '', receipt_date: '', reconciled: 'N' });

  const [linesOpen, setLinesOpen] = useState(false);
  const [lines, setLines] = useState([]);
  const [linesTitle, setLinesTitle] = useState('');

  useEffect(() => {
    fetchReceipts({ name: '', receipt_date: '', reconciled: 'N' });
    fetchReceiptTypes();
  }, []);

  const fetchReceipts = async (filters) => {
    try {
      const res = await axios.get(`${API_URL}/receipts`, {
        params: filters !== undefined ? filters : searchFilters,
        ...auth(),
      });
      setReceipts(res.data);
    } catch (e) {
      console.error('Error fetching receipts:', e);
    }
  };

  const fetchReceiptTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/commonLOVs/payment-types`, {
        params: { from_to: 'F' },
        ...auth(),
      });
      setReceiptTypes(res.data);
    } catch (e) {
      console.error('Error fetching receipt types:', e);
    }
  };

  const fetchLines = async (id, name) => {
    try {
      const res = await axios.get(`${API_URL}/receipts/lines/${id}`, auth());
      setLines(res.data);
      setLinesTitle(name);
      setLinesOpen(true);
    } catch (e) {
      console.error('Error fetching receipt lines:', e);
    }
  };

  const handleSearchChange = (e) => {
    const updated = { ...searchFilters, [e.target.name]: e.target.value };
    setSearchFilters(updated);
    fetchReceipts(updated);
  };

  const handleReset = () => {
    const reset = { name: '', receipt_date: '', reconciled: '' };
    setSearchFilters(reset);
    fetchReceipts(reset);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'reconciled') {
      setShowIncomeType(form.reconciled === 'N' && value === 'Y');
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = (r) => {
    setForm({ ...r, receipt_date: fmtDate(r.receipt_date) });
    setShowIncomeType(false);
    setDisableReconciled(r.reconciled === 'Y');
    setEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/receipts/${form.id}`, form, auth());
      setForm(emptyForm);
      setEditing(false);
      setShowIncomeType(false);
      setDisableReconciled(false);
      fetchReceipts();
    } catch (e) {
      console.error('Error saving receipt:', e);
    }
  };

  return (
    <>
      <div className="bt-fm-header">
        <span className="bt-fm-title">Receipts</span>
        <button className="bt-fm-close" onClick={onClose}>×</button>
      </div>

      <div className="bt-fm-body">
        {/* Edit form */}
        <h3 className="bt-panel-h">Edit Receipt</h3>
        <form onSubmit={handleSubmit}>
          <div className="bt-form-row">
            <input
              type="text" name="name" value={form.name}
              placeholder="Receipt Name" readOnly className="bt-input"
            />
            <input
              type="date" name="receipt_date" value={form.receipt_date}
              readOnly className="bt-input"
            />
            <input
              type="text" name="receipt_amount" value={form.receipt_amount}
              placeholder="Amount" readOnly className="bt-input bt-input-sm"
            />
            <input
              type="text" name="comments" value={form.comments || ''}
              placeholder="Comments" readOnly={!editing}
              onChange={handleChange} className="bt-input"
            />
            <select
              name="reconciled" value={form.reconciled}
              onChange={handleChange}
              disabled={!editing || disableReconciled}
              className="bt-select"
            >
              <option value="">Select</option>
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </select>
            {showIncomeType && (
              <select
                name="income_type_id" value={form.income_type_id || ''}
                onChange={handleChange} required className="bt-select"
              >
                <option value="">Select Income Type</option>
                {receiptTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
          </div>
          <button type="submit" className="bt-btn-primary bt-mt-8" disabled={!editing}>
            Update Receipt
          </button>
        </form>

        {/* Search */}
        <h3 className="bt-panel-h bt-mt-20">Receipts List</h3>
        <div className="bt-form-row bt-mb-12">
          <input
            type="text" name="name" value={searchFilters.name}
            placeholder="Name" onChange={handleSearchChange} className="bt-input"
          />
          <input
            type="date" name="receipt_date" value={searchFilters.receipt_date}
            onChange={handleSearchChange} className="bt-input"
          />
          <select
            name="reconciled" value={searchFilters.reconciled}
            onChange={handleSearchChange} className="bt-select"
          >
            <option value="">All</option>
            <option value="Y">Yes</option>
            <option value="N">No</option>
          </select>
          <button className="bt-btn-secondary" onClick={handleReset}>Reset</button>
        </div>

        {/* Table */}
        <div className="bt-table-wrap">
          <table className="bt-table">
            <thead>
              <tr>
                <th>Receipt Name</th>
                <th>Account Number</th>
                <th>Receipt Date</th>
                <th style={{ width: '80px' }}>Receipt Amount</th>
                <th style={{ width: '80px' }}>Balance Amount</th>
                <th>Reconciled</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {receipts.length === 0 ? (
                <tr><td colSpan="7" className="bt-td-empty">No records found.</td></tr>
              ) : receipts.map(r => (
                <tr key={r.id}>
                  <td>
                    <button className="bt-link" onClick={() => fetchLines(r.id, r.name)}>
                      {r.name}
                    </button>
                  </td>
                  <td>{r.account_no}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.receipt_date)}</td>
                  <td>{r.receipt_amount}</td>
                  <td>{r.balance_amount}</td>
                  <td>{r.reconciled}</td>
                  <td>
                    <button className="bt-btn-sm" onClick={() => handleEdit(r)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lines sub-modal */}
      {linesOpen && (
        <div className="bt-sub-overlay" onClick={() => setLinesOpen(false)}>
          <div className="bt-submodal" onClick={e => e.stopPropagation()}>
            <div className="bt-submodal-hdr">
              <span className="bt-submodal-title">Receipt Lines: {linesTitle}</span>
              <button className="bt-fm-close" onClick={() => setLinesOpen(false)}>×</button>
            </div>
            <div className="bt-submodal-body">
              <table className="bt-table">
                <thead>
                  <tr>
                    <th>Receipt Type</th>
                    <th>Detail</th>
                    <th>Name</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr><td colSpan="4" className="bt-td-empty">No lines found.</td></tr>
                  ) : lines.map(l => (
                    <tr key={l.id}>
                      <td>{l.receipt_type}</td>
                      <td>{l.detail}</td>
                      <td>{l.name}</td>
                      <td>{l.receipt_amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
